import SaveTemplateIcon from '@mui/icons-material/SaveOutlined';
import {
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Tab,
    Tabs,
    TextField,
} from '@mui/material';
import { useOktaAuth } from '@okta/okta-react';
import { Ace } from 'ace-builds';
import React, { useEffect, useState } from 'react';
import AceEditor from 'react-ace';
import { useTranslation } from 'react-i18next';

import 'ace-builds/src-min-noconflict/ext-searchbox';
import 'ace-builds/src-noconflict/ext-error_marker';
import 'ace-builds/src-noconflict/ext-language_tools';
import 'ace-builds/src-noconflict/mode-json';
import 'ace-builds/src-noconflict/snippets/json';
import 'ace-builds/src-noconflict/theme-terminal';

import Backend from '../backend/Backend';
import DataSourceType from '../DataSourceType';
import { ReactComponent as ExpandeTemplateIcon } from '../resources/images/expand-template-icon.svg';
import LookupFileManager from './LookupFileManager';
import SFExplorer from './SFExplorer';
import './TemplateJsonEditor.scss';

interface TemplateJsonEditorProps {
    config: any;
    progress: Function;
    hasSaveBtn?: boolean;
    setParentTemplateJson?: Function;
    setIsTemplateExpanded?: () => void;
}

const TemplateJsonEditor = (props: TemplateJsonEditorProps) => {
    const { t } = useTranslation();
    const { authState, oktaAuth } = useOktaAuth();
    const [template_json, setTemplateJson] = useState<any>(props.config.environments[0].template.mapping_raw_json);
    const [orginal_template_json, setOriginalTemplateJson] = useState<any>(
        props.config.environments[0].template.mapping_raw_json,
    );
    const [isJsonValid, setIsJsonValid] = useState<boolean>(true);
    const [open, setOpenConfirmationDialog] = useState<boolean>(false);
    const [update_description, setUpdateDescription] = useState<string | null>(null);

    const [tabIndex, setTabIndex] = useState(0);

    useEffect(() => {
        setTemplateJson(props.config.environments[0].template.mapping_raw_json);
    }, [props.config.environments[0].template.mapping_raw_json]);

    const handleTabChange = (event: React.SyntheticEvent, newIndex: number) => {
        setTabIndex(newIndex);
    };

    function handleJsonChange(json: any) {
        console.log(json);
        setTemplateJson(json);
        if (props.setParentTemplateJson) {
            props.setParentTemplateJson(json);
        }
    }

    function handleJsonValidate(annotations: Ace.Annotation[]) {
        if (template_json !== orginal_template_json) {
            setIsJsonValid(annotations.length > 0);
        } else {
            setIsJsonValid(true);
        }
    }

    async function updateTemplate() {
        props.progress(true);
        setIsJsonValid(true);

        const response = await Backend.updateTemplate(
            props.config.rowid,
            JSON.parse(template_json),
            template_json,
            update_description,
            oktaAuth,
        );
        console.log(response);

        const { template } = props.config.environments[0];

        // save a copy back on to original list
        template.mapping = JSON.parse(JSON.stringify(response.data.template));
        template.mapping_raw_json = `${response.data.template_raw_json}`;

        setOriginalTemplateJson(template_json);

        props.progress(false);
    }

    function handleDismissConfirmationDialog() {
        setOpenConfirmationDialog(false);
    }

    function handleConfirmSave() {
        setOpenConfirmationDialog(false);
        updateTemplate();
    }

    return (
        <div className="TemplateJsonEditorRoot">
            <Box
                sx={{
                    borderBottom: 1,
                    borderColor: 'divider',
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                }}
            >
                <Tabs value={tabIndex} onChange={handleTabChange}>
                    <Tab label="Template Editor" id="0" />
                    <Tab label="Lookup Manager" id="1" />
                    {(props.config.config_type === DataSourceType.sfdc ||
                        props.config.config_type === DataSourceType.svmx ||
                        props.config.config_type === DataSourceType.custom) && <Tab label="Model Explorer" id="2" />}
                </Tabs>
                <IconButton sx={{ justifyContent: 'end' }} onClick={() => props.setIsTemplateExpanded()}>
                    <ExpandeTemplateIcon />
                </IconButton>
            </Box>
            <div role="tabpanel" className="TabPanel" hidden={tabIndex !== 0}>
                <Card style={{ width: '100%' }}>
                    <CardContent style={{ width: '100%' }}>
                        <AceEditor
                            style={{ width: '100%', height: '700px' }}
                            mode="json"
                            theme="terminal"
                            onChange={(json: any) => {
                                handleJsonChange(json);
                            }}
                            fontSize={18}
                            showPrintMargin
                            showGutter
                            highlightActiveLine
                            value={template_json}
                            setOptions={{
                                enableBasicAutocompletion: true,
                                enableLiveAutocompletion: true,
                                enableSnippets: true,
                                showLineNumbers: true,
                                tabSize: 4,
                                useWorker: true,
                            }}
                            onValidate={(annotations: Ace.Annotation[]) => {
                                handleJsonValidate(annotations);
                            }}
                        />
                    </CardContent>
                    {props.hasSaveBtn ? (
                        <CardActions>
                            <Button
                                disabled={isJsonValid}
                                onClick={() => {
                                    setOpenConfirmationDialog(true);
                                }}
                                variant="contained"
                                startIcon={<SaveTemplateIcon />}
                            >
                                {t('common.save')}
                            </Button>
                        </CardActions>
                    ) : null}
                </Card>
                <Dialog open={open} onClose={handleDismissConfirmationDialog}>
                    <DialogTitle>{t('common.confirm')}</DialogTitle>
                    <DialogContent>
                        <DialogContentText>{t('template.save-description')}</DialogContentText>
                        <TextField
                            autoFocus
                            margin="dense"
                            fullWidth
                            variant="standard"
                            onChange={(e) => setUpdateDescription(e.target.value)}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleDismissConfirmationDialog}>{t('common.cancel')}</Button>
                        <Button
                            disabled={!update_description || update_description.trim().length === 0}
                            onClick={handleConfirmSave}
                        >
                            {t('common.save')}
                        </Button>
                    </DialogActions>
                </Dialog>
            </div>

            <div role="tabpanel" className="TabPanel" hidden={tabIndex !== 1}>
                <Card style={{ width: '100%' }}>
                    <CardContent style={{ width: '100%' }}>
                        <LookupFileManager hidden={tabIndex !== 1} progress={props.progress} />
                    </CardContent>
                </Card>
            </div>

            {(props.config.config_type === DataSourceType.sfdc ||
                props.config.config_type === DataSourceType.svmx ||
                props.config.config_type === DataSourceType.custom) && (
                <div role="tabpanel" className="TabPanel" hidden={tabIndex !== 2}>
                    <Card style={{ width: '100%' }}>
                        <CardContent style={{ width: '100%' }}>
                            <SFExplorer hidden={tabIndex !== 2} config={props.config} progress={props.progress} />
                        </CardContent>
                    </Card>
                </div>
            )}
        </div>
    );
};

// Setting default value for props
TemplateJsonEditor.defaultProps = {
    hasSaveBtn: true,
};

export default TemplateJsonEditor;
